import type { SitWithRequest } from '@league/shared/contract';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button, ErrorNote } from '../../components/ui';
import { ApiError, apiPost } from '../../lib/api';
import { comparePeople } from './people-display';

interface Person { enrollmentId: string; name: string; firstName: string; lastName: string; email: string | null; enrolled: boolean }

/*
  PLAY TOGETHER REQUESTS NOT IN A GROUP YET (2026-10-25). A player names a friend on the
  registration form; when the friend is on the roster the platform groups them itself, and what is
  left — a name that matches nobody yet, or a friend still waiting for a seat — lands here, so it
  is never invisible. Matching puts the two in a Play Together group above.
*/
export function PlayTogetherRequests({ leagueId, requests, people }: { leagueId: string; requests: SitWithRequest[]; people: Person[] }) {
  const client = useQueryClient();
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const match = useMutation({
    mutationFn: ({ requestId, enrollmentId }: { requestId: string; enrollmentId: string }) =>
      apiPost(`/api/sit-with-requests/${requestId}/resolve`, { enrollmentId }),
    onSuccess: () => Promise.all([
      client.invalidateQueries({ queryKey: ['roster', leagueId] }),
      client.invalidateQueries({ queryKey: ['play-together-groups', leagueId] }),
    ]),
  });
  if (requests.length === 0) return null;

  const onRoster = people.filter((p) => p.enrolled).sort(comparePeople);
  /* Who she probably meant: the friend she picked, else an exact email, else one exact name. */
  const likely = (request: SitWithRequest) => {
    const others = onRoster.filter((p) => p.enrollmentId !== request.fromEnrollmentId);
    if (request.toEnrollmentId) return others.find((p) => p.enrollmentId === request.toEnrollmentId)?.enrollmentId ?? '';
    const email = request.requestedEmail?.trim().toLowerCase();
    const byEmail = email ? others.find((p) => p.email?.toLowerCase() === email) : undefined;
    if (byEmail) return byEmail.enrollmentId;
    const name = request.requestedName?.trim().toLowerCase().replace(/\s+/g, ' ');
    const byName = name ? others.filter((p) => p.name.toLowerCase().replace(/\s+/g, ' ') === name) : [];
    return byName.length === 1 ? byName[0]!.enrollmentId : '';
  };

  return (
    <div className="flex flex-col gap-2">
      <h4 className="font-ui text-[12px] font-semibold uppercase tracking-[0.1em] text-ink-2">Requests</h4>
      {match.error ? <ErrorNote>{match.error instanceof ApiError ? match.error.message : 'Could not match them.'}</ErrorNote> : null}
      <ul className="flex flex-col divide-y divide-light rounded-md border border-light bg-paper">
        {requests.map((request) => {
          const asked = request.toName ?? request.requestedName ?? request.requestedEmail ?? '';
          const value = chosen[request.id] ?? likely(request);
          return <li key={request.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
            <span className="text-[14px]">
              {request.fromName} → {asked}
              {request.requestedEmail && request.requestedEmail !== asked ? <span className="text-ink-2"> · {request.requestedEmail}</span> : null}
            </span>
            <span className="flex flex-wrap items-center gap-2">
              <select
                aria-label={`Match ${request.fromName}’s request to`}
                value={value}
                onChange={(e) => { match.reset(); setChosen((c) => ({ ...c, [request.id]: e.target.value })); }}
                className="min-h-11 min-w-0 rounded-md border border-control-border bg-paper px-3 text-[14px]"
              >
                <option value="">Choose a player</option>
                {onRoster.filter((p) => p.enrollmentId !== request.fromEnrollmentId).map((p) =>
                  <option key={p.enrollmentId} value={p.enrollmentId}>{p.name}</option>)}
              </select>
              <Button variant="secondary" disabled={!value || match.isPending}
                onClick={() => match.mutate({ requestId: request.id, enrollmentId: value })}>Match</Button>
            </span>
          </li>;
        })}
      </ul>
    </div>
  );
}
